import { assertCompleteRows } from "./aggregateState";
// ==========================================================================
// lib/household.ts — React Query hooks for the core tables (FF-025)
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useAuth } from "./auth";

export type Person = { id: string; display_name: string; role: "adult" | "child"; wfh_default_hours: number; colour: string; born_on?: string | null; full_name?: string | null; mobile?: string | null };
export type Entity = { id: string; name: string; kind: "individual" | "trust" | "company"; is_external: boolean; notes: string | null };
export type Audit = { id: number; table_name: string; action: string; at: string; actor_person_id: string | null };

export function useHouseholdId() {
  return useAuth().householdIds[0];
}

/** This login's membership row: which person it is and its colour theme.
 *  Filtered by auth_user_id (fixed 9 Oct: it used to take the first member,
 *  which would have been wrong once Caitlin has her own login). */
export type Theme = "rose" | "lilac" | "forest" | "blue";
export function useMembership() {
  const hid = useHouseholdId(); const uid = useAuth().session?.user.id;
  return useQuery({
    queryKey: ["membership", hid, uid], enabled: !!hid && !!uid,
    queryFn: async () => {
      const { data, error } = await supabase.from("household_memberships").select("person_id,theme,role").eq("household_id", hid).eq("auth_user_id", uid!).is("revoked_at", null).maybeSingle();
      if (error) throw error;
      return data as { person_id: string; theme: Theme; role: "admin" | "member" } | null;
    },
  });
}

/** The person row behind this login (from the membership it claimed). */
export function useMe() {
  const people = usePeople(); const m = useMembership();
  return people.data?.find((p) => p.id === m.data?.person_id);
}

export const THEMES: { key: Theme; label: string; swatch: string }[] = [
  { key: "rose", label: "Rose", swatch: "#C44264" },
  { key: "lilac", label: "Lilac", swatch: "#7651B8" },
  { key: "forest", label: "Forest", swatch: "#2E6A4D" },
  { key: "blue", label: "Blue", swatch: "#2F5FA8" }, // FF-058
];

/** Puts the theme on <html>. Also kept in this browser so the right colour shows before the data loads. */
export function applyTheme(t: Theme) {
  document.documentElement.dataset.theme = t;
  try { localStorage.setItem("theme", t); } catch { /* private window: fine */ }
}

export function useSetTheme() {
  const qc = useQueryClient(); const hid = useHouseholdId();
  return useMutation({
    mutationFn: async (t: Theme) => {
      applyTheme(t);
      const { error } = await supabase.rpc("set_my_theme", { p_theme: t });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["membership", hid] }),
  });
}

/** Everyone's login in the household (FF-059): who signs in as what, and invites not used yet. */
export type Login = { id: string; person_id: string; invited_email: string; role: "admin" | "member"; accepted_at: string | null };
export function useLogins() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["logins", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("household_memberships").select("id,person_id,invited_email,role,accepted_at").eq("household_id", hid).is("revoked_at", null);
      if (error) throw error;
      return data as Login[];
    },
  });
}

/** Admin only, checked by the database (036). The sign-in itself is made in the Supabase dashboard. */
export function useInviteMember() {
  const qc = useQueryClient(); const hid = useHouseholdId();
  return useMutation({
    mutationFn: async (a: { personId: string; email: string }) => {
      const { error } = await supabase.rpc("invite_member", { p_person_id: a.personId, p_email: a.email });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logins", hid] }),
  });
}

export function useCancelInvite() {
  const qc = useQueryClient(); const hid = useHouseholdId();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("cancel_invite", { p_membership_id: id });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["logins", hid] }),
  });
}

/** My profile (FF-058): the details on the signed-in person's own row. */
export type ProfileDraft = { display_name: string; full_name: string | null; mobile: string | null; born_on: string | null; colour: string; wfh_default_hours: number };
export function useSaveProfile() {
  const qc = useQueryClient(); const hid = useHouseholdId(); const m = useMembership();
  return useMutation({
    mutationFn: async (d: ProfileDraft) => {
      const id = m.data?.person_id; if (!id) throw new Error("This login isn't linked to a person yet.");
      const { error } = await supabase.from("people").update(d).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people", hid] }),
  });
}

/** The person colours (people.colour). 7 and 8 added in 039 (10 Oct): blossom pink and teal. */
export const PERSON_COLOURS = [
  { key: "chart-1", label: "Rose" }, { key: "chart-7", label: "Blossom" }, { key: "chart-5", label: "Purple" }, { key: "chart-4", label: "Blue" },
  { key: "chart-8", label: "Teal" }, { key: "chart-3", label: "Green" }, { key: "chart-6", label: "Gold" }, { key: "chart-2", label: "Orange" },
] as const;

/** Anyone's colour, from the Family screen (children have no profile of their own). */
export function useSetPersonColour() {
  const qc = useQueryClient(); const hid = useHouseholdId();
  return useMutation({
    mutationFn: async ({ id, colour }: { id: string; colour: string }) => {
      const { error } = await supabase.from("people").update({ colour }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people", hid] }),
  });
}

export function useHousehold() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["household", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("households").select("id,name,timezone").eq("id", hid).single();
      if (error) throw error;
      return data as { id: string; name: string; timezone: string };
    },
  });
}

export function usePeople() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["people", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("people").select("id,display_name,role,wfh_default_hours,colour,born_on,full_name,mobile").eq("household_id", hid).is("deleted_at", null).order("role").order("display_name");
      if (error) throw error;
      return data as Person[];
    },
  });
}

export function useEntities() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["entities", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error, count } = await supabase.from("entities").select("id,name,kind,is_external,notes", { count: "exact" }).eq("household_id", hid).is("deleted_at", null).order("kind").order("name");
      if (error) throw error;
      assertCompleteRows({ data, count }, "entities");
      return data as Entity[];
    },
  });
}

export function useRecentChanges(limit = 8) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["audit", hid, limit], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("audit_events").select("id,table_name,action,at,actor_person_id").eq("household_id", hid).order("at", { ascending: false }).limit(limit);
      if (error) throw error;
      return data as Audit[];
    },
  });
}

/** "households" -> "household", "entity_ownerships" -> "entity ownership" */
export function plainTable(name: string) {
  return name.replace(/_/g, " ").replace(/ies$/, "y").replace(/s$/, "");
}

export type EntityOwnership = { id: string; owner_entity_id: string; owned_entity_id: string; interest: string; share_pct: number | null; valid_from: string | null; valid_to: string | null };

export function useEntityOwnerships() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["entity_ownerships", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("entity_ownerships").select("id,owner_entity_id,owned_entity_id,interest,share_pct,valid_from,valid_to").eq("household_id", hid).is("deleted_at", null);
      if (error) throw error;
      return (data as EntityOwnership[]).map((o) => ({ ...o, share_pct: o.share_pct === null ? null : Number(o.share_pct) }));
    },
  });
}
